import { useState, type FC } from "react";
import { Bell, CircleCheck, FileText, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/shared/components/ui/sheet";
import { useNotifications } from "@/shared/hooks/useNotifications";
import { useMarkNotificationsRead } from "@/shared/hooks/useMarkNotificationsRead";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { formatRelativeTime } from "@/shared/utils/formatters";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";
import type { Notification } from "@/shared/types/notificationTypes";
import { IconButton } from "@/shared/components/ui/icon-button";

function getNotificationTitle(notification: Notification): string {
  const type = notification.metadata.document_type;
  if (!type || type === "other") {
    return "Your document has finished processing.";
  }
  const label = getDocumentTypeLabel(type).toLowerCase();
  return `Your ${label} has finished processing.`;
}

interface NotificationCardProps {
  notification: Notification;
  onNavigate: () => void;
}

const NotificationCard: FC<NotificationCardProps> = ({ notification, onNavigate }) => {
  const isUnread = notification.read_at === null;

  return (
    <button
      className="w-full text-left flex items-start gap-3 px-3 py-2 rounded-md transition-colors hover:bg-subtle"
      onClick={onNavigate}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white border border-primary text-secondary">
        <FileText className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="leading-[1.55]">{getNotificationTitle(notification)}</p>
        <p className="text-tertiary text-xs leading-[1.55] mt-0.5">
          {formatRelativeTime(notification.created_at)} · Click to view insights
        </p>
      </div>
      <span className="flex h-2 w-2 shrink-0 mt-[5px]" aria-hidden="true">
        {isUnread && <span className="h-2 w-2 rounded-full bg-brand" />}
      </span>
    </button>
  );
};

interface NotificationBellProps {
  variant?: "pill" | "icon";
}

export const NotificationBell: FC<NotificationBellProps> = ({ variant = "pill" }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { person, user } = useAppContext();

  const estateId = person?.estate_id;
  const userId = user?.user_id;

  const { data } = useNotifications(estateId, userId);
  const { mutate: markRead } = useMarkNotificationsRead(estateId);

  const ID_DOCUMENT_TYPES = new Set(["identification_card", "decedent_id"]);

  const notifications = (data?.notifications ?? []).filter(
    (n) => !ID_DOCUMENT_TYPES.has(n.metadata.document_type ?? ""),
  );
  const unreadCount = notifications.filter((n) => n.read_at === null).length;

  const sorted = [
    ...notifications
      .filter((n) => n.read_at === null)
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    ...notifications
      .filter((n) => n.read_at !== null)
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
  ].slice(0, 6);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next && unreadCount > 0) {
      markRead();
    }
  };

  const handleNavigate = (notification: Notification) => {
    handleOpenChange(false);
    if (notification.entity_id) {
      queryClient.invalidateQueries({
        queryKey: ["documentMetadata", notification.entity_id],
      });
    }
    navigate(`${POST_LOSS_ROUTES.DOCUMENTS}?doc=${notification.entity_id}`);
  };

  const notificationContent = (
    <>
      {sorted.length === 0 ? (
        <div className="flex flex-col flex-1 items-center justify-center gap-3 min-h-[200px]">
          <CircleCheck className="h-8 w-8 text-disabled" />
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm font-medium">You&apos;re all caught up</p>
            <p className="text-xs text-secondary">No new notifications</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col px-2">
          {sorted.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onNavigate={() => handleNavigate(notification)}
            />
          ))}
        </div>
      )}
    </>
  );

  if (variant === "icon") {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <button
            className="relative flex items-center justify-center w-8 h-8 rounded-full border border-primary bg-white text-primary hover:bg-subtle transition-colors"
            aria-label="Notifications"
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-brand" />
            )}
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          showClose={false}
          className="w-full max-w-none rounded-t-lg p-0 border border-primary border-b-0 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] top-8 flex flex-col"
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0">
            <SheetTitle asChild>
              <p className="text-base font-medium">Notifications</p>
            </SheetTitle>
            <IconButton
              onClick={() => handleOpenChange(false)}
              label="Close notifications"
              tooltip={false}
            >
              <X />
            </IconButton>
          </div>
          <div className="flex-1 overflow-y-auto pb-2">{notificationContent}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary text-sm text-primary hover:bg-subtle transition-colors h-8">
          <Bell size={14} />
          <span>Notifications</span>
          {unreadCount > 0 && (
            <span className="flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-brand text-white text-[10px] font-medium leading-none">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        className="w-96 min-h-[480px] p-0 overflow-hidden flex flex-col shadow-[0_8px_28px_rgba(0,0,0,0.12)]"
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <p className="text-base font-medium">Notifications</p>
          <IconButton
            onClick={() => handleOpenChange(false)}
            label="Close notifications"
            tooltip={false}
          >
            <X />
          </IconButton>
        </div>
        {notificationContent}
      </PopoverContent>
    </Popover>
  );
};
